import { getAccessToken } from '../api/client'

// One WebSocket per browser tab, shared by every useWebSocket subscriber.
// Each component used to open its own socket, and an unmounted component's
// socket reconnected forever (onclose fired after cleanup and scheduled a new
// connect) — pages leaked sockets until the server's per-IP cap (50) refused
// them. The connection opens with the first subscriber, closes with the last,
// and reconnects with capped exponential backoff while anyone is listening.

export interface Subscriber {
  /** One parsed message (the server batches several per frame, newline-separated). */
  onMessage?: (data: unknown) => void
  onOpen?: () => void
  onClose?: () => void
}

const MIN_BACKOFF_MS = 1000
const MAX_BACKOFF_MS = 30000

const subscribers = new Set<Subscriber>()
let socket: WebSocket | null = null
let reconnectTimer: ReturnType<typeof setTimeout> | null = null
let attempt = 0

export function isOpen(): boolean {
  return socket?.readyState === WebSocket.OPEN
}

/** Delay before reconnect attempt n (0-based): 1s, 2s, 4s … capped at 30s, ±20% jitter. */
export function backoffDelay(n: number, random: () => number = Math.random): number {
  const base = Math.min(MAX_BACKOFF_MS, MIN_BACKOFF_MS * 2 ** n)
  return Math.round(base * (0.8 + random() * 0.4))
}

/** Splits a frame into its newline-separated JSON messages; bad lines are skipped. */
export function parseFrame(raw: string): unknown[] {
  const out: unknown[] = []
  for (const line of raw.split('\n')) {
    if (!line.trim()) continue
    try {
      out.push(JSON.parse(line))
    } catch {
      // A malformed line must not drop the rest of the batch.
    }
  }
  return out
}

function open() {
  if (socket || subscribers.size === 0) return
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const token = getAccessToken()
  const ws = new WebSocket(`${protocol}//${window.location.host}/api/ws${token ? `?token=${encodeURIComponent(token)}` : ''}`)
  socket = ws

  ws.onopen = () => {
    attempt = 0
    subscribers.forEach((s) => s.onOpen?.())
  }
  ws.onmessage = (event) => {
    for (const msg of parseFrame(String(event.data))) {
      subscribers.forEach((s) => s.onMessage?.(msg))
    }
  }
  ws.onclose = () => {
    if (socket === ws) socket = null
    subscribers.forEach((s) => s.onClose?.())
    scheduleReconnect()
  }
  ws.onerror = () => {
    // onclose follows and handles the reconnect.
  }
}

function scheduleReconnect() {
  if (reconnectTimer || subscribers.size === 0) return
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    open()
  }, backoffDelay(attempt++))
}

/** Adds a subscriber (opening the shared socket if needed); returns the unsubscribe. */
export function subscribe(sub: Subscriber): () => void {
  subscribers.add(sub)
  if (isOpen()) sub.onOpen?.()
  open()
  return () => {
    subscribers.delete(sub)
    if (subscribers.size === 0) {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer)
        reconnectTimer = null
      }
      const ws = socket
      socket = null
      attempt = 0
      if (ws) {
        ws.onclose = null
        ws.close()
      }
    }
  }
}

export function send(data: unknown) {
  if (isOpen()) socket!.send(JSON.stringify(data))
}

/** Test-only: drop all state. */
export function resetForTests() {
  subscribers.clear()
  if (reconnectTimer) clearTimeout(reconnectTimer)
  reconnectTimer = null
  socket = null
  attempt = 0
}
